'use client';
import { useState, useEffect, useMemo } from 'react';
import { useReadContract, useWriteContract, useAccount, useWaitForTransactionReceipt, useChainId, useSwitchChain } from 'wagmi';
import { parseEther, formatEther, Address } from 'viem';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/sheet';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Label } from '@/components/label';
import { RiLoader4Line, RiCheckLine, RiCloseLine, RiImageLine, RiAlertLine } from '@remixicon/react';
import { ERC721_ABI } from '@/abis/erc721';
import { NFT_MARKETPLACE_CONTRACT_CONFIG } from '@/configs/nft-marketplace.config';
import { Card, CardContent } from '@/components/card';
import { Badge } from '@/components/badge';
import { larissaChainRainbow } from '@/configs/chain';

interface ListNftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ListNftModal({ isOpen, onClose, onSuccess }: ListNftModalProps) {
  const { address: userAccount } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const [contractAddress, setContractAddress] = useState('');
  const [tokenId, setTokenId] = useState('');
  const [price, setPrice] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'contract' | 'details' | 'price' | 'approve' | 'list'>('contract');

  // Transaction hashes
  const [approveTxHash, setApproveTxHash] = useState<`0x${string}` | undefined>(undefined);
  const [listTxHash, setListTxHash] = useState<`0x${string}` | undefined>(undefined);

  const { writeContractAsync: writeContract } = useWriteContract();

  // Wait for approval transaction
  const { isLoading: isApprovePending, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({
    hash: approveTxHash,
    query: {
      enabled: !!approveTxHash,
    },
  });

  // Wait for list transaction
  const { isLoading: isListPending, isSuccess: isListSuccess } = useWaitForTransactionReceipt({
    hash: listTxHash,
    query: {
      enabled: !!listTxHash,
    },
  });

  // Fetch NFT owner
  const { data: nftOwner, isLoading: isLoadingOwner } = useReadContract({
    address: contractAddress && contractAddress.startsWith('0x') ? (contractAddress as Address) : undefined,
    abi: ERC721_ABI,
    functionName: 'ownerOf',
    args: tokenId ? [BigInt(tokenId)] : undefined,
    query: {
      enabled: !!contractAddress && !!tokenId && contractAddress.startsWith('0x') && step !== 'contract',
    },
  }) as { data: Address | undefined; isLoading: boolean };

  // Fetch NFT tokenURI
  const { data: tokenURI, isLoading: isLoadingURI } = useReadContract({
    address: contractAddress && contractAddress.startsWith('0x') ? (contractAddress as Address) : undefined,
    abi: ERC721_ABI,
    functionName: 'tokenURI',
    args: tokenId ? [BigInt(tokenId)] : undefined,
    query: {
      enabled: !!contractAddress && !!tokenId && contractAddress.startsWith('0x') && step !== 'contract',
    },
  }) as { data: string | undefined; isLoading: boolean };

  // Fetch NFT approval status
  const { data: approvedAddress, refetch: refetchApproval } = useReadContract({
    address: contractAddress && contractAddress.startsWith('0x') ? (contractAddress as Address) : undefined,
    abi: ERC721_ABI,
    functionName: 'getApproved',
    args: tokenId ? [BigInt(tokenId)] : undefined,
    query: {
      enabled: !!contractAddress && !!tokenId && contractAddress.startsWith('0x') && step !== 'contract',
    },
  }) as { data: Address | undefined; refetch: () => void };

  // Fetch NFT metadata
  const [nftMetadata, setNftMetadata] = useState<any>(null);
  const [nftImage, setNftImage] = useState<string | null>(null);

  useEffect(() => {
    if (tokenURI && step !== 'contract') {
      const fetchMetadata = async () => {
        try {
          let url = tokenURI;
          if (url.startsWith('ipfs://')) {
            url = `https://ipfs.io/ipfs/${url.replace('ipfs://', '')}`;
          }

          const response = await fetch(url);
          if (!response.ok) throw new Error('Failed to fetch metadata');

          const contentType = response.headers.get('content-type');
          let metadata: any;

          if (contentType && contentType.includes('application/json')) {
            metadata = await response.json();
          } else {
            const text = await response.text();
            metadata = JSON.parse(text);
          }

          setNftMetadata(metadata);

          if (metadata.image) {
            let imageUrl = metadata.image;
            if (imageUrl.startsWith('ipfs://')) {
              imageUrl = `https://ipfs.io/ipfs/${imageUrl.replace('ipfs://', '')}`;
            }
            setNftImage(imageUrl);
          }
        } catch (error) {
          console.error('Error fetching NFT metadata:', error);
          setNftMetadata(null);
          setNftImage(null);
        }
      };

      fetchMetadata();
    }
  }, [tokenURI, step]);

  // Check if user owns the NFT
  const isOwner = useMemo(() => {
    return nftOwner && userAccount && nftOwner.toLowerCase() === userAccount.toLowerCase();
  }, [nftOwner, userAccount]);

  // Check if marketplace is approved
  const isApproved = useMemo(() => {
    return approvedAddress && NFT_MARKETPLACE_CONTRACT_CONFIG.address &&
           approvedAddress.toLowerCase() === NFT_MARKETPLACE_CONTRACT_CONFIG.address.toLowerCase();
  }, [approvedAddress]);

  // Handle Larissa Dao NFT suggestion
  const handleUseLarissaDaoNft = () => {
    const larissaDaoAddress = import.meta.env.VITE_NFT_DAO_V2_CONTRACT_ADDRESS || '';
    if (larissaDaoAddress) {
      setContractAddress(larissaDaoAddress);
      setError('');
    }
  };

  // Validate and move to next step
  const handleContractSubmit = () => {
    if (!contractAddress || !contractAddress.startsWith('0x')) {
      setError('Please enter a valid contract address');
      return;
    }
    setError('');
    setStep('details');
  };

  const handleTokenIdSubmit = () => {
    if (!tokenId || isNaN(Number(tokenId)) || Number(tokenId) < 0) {
      setError('Please enter a valid token ID');
      return;
    }
    setError('');
    // Wait for owner check
    if (isOwner === false) {
      setError('You are not the owner of this NFT');
      return;
    }
    if (isOwner === true) {
      setStep('price');
    }
  };

  const handlePriceSubmit = () => {
    if (!price || isNaN(Number(price)) || Number(price) <= 0) {
      setError('Please enter a valid price (greater than 0)');
      return;
    }
    setError('');
    setStep('approve');
  };

  // Handle approve
  const handleApprove = async () => {
    if (!contractAddress || !tokenId || !userAccount) {
      setError('Missing required information');
      return;
    }

    if (chainId !== larissaChainRainbow.id) {
      switchChain({ chainId: larissaChainRainbow.id });
      return;
    }

    setError('');
    setApproveTxHash(undefined);

    try {
      const hash = await writeContract({
        address: contractAddress as Address,
        abi: ERC721_ABI,
        functionName: 'approve',
        args: [NFT_MARKETPLACE_CONTRACT_CONFIG.address, BigInt(tokenId)],
      });
      setApproveTxHash(hash);
    } catch (err) {
      console.error('Approval error:', err);
      setError(err instanceof Error ? err.message : 'Approval failed');
    }
  };

  // Handle list NFT
  const handleListNft = async () => {
    if (!contractAddress || !tokenId || !price || !userAccount) {
      setError('Missing required information');
      return;
    }

    if (chainId !== larissaChainRainbow.id) {
      switchChain({ chainId: larissaChainRainbow.id });
      return;
    }

    if (!isApproved) {
      setError('Please approve the NFT first');
      return;
    }

    setError('');
    setListTxHash(undefined);

    try {
      const priceInWei = parseEther(price);
      const hash = await writeContract({
        address: NFT_MARKETPLACE_CONTRACT_CONFIG.address,
        abi: NFT_MARKETPLACE_CONTRACT_CONFIG.abi,
        functionName: 'listNFT',
        args: [BigInt(tokenId), priceInWei, contractAddress as Address],
      });
      setListTxHash(hash);
    } catch (err) {
      console.error('List NFT error:', err);
      setError(err instanceof Error ? err.message : 'Failed to list NFT');
    }
  };

  // Handle approval success
  useEffect(() => {
    if (isApproveSuccess) {
      refetchApproval();
      setStep('list');
    }
  }, [isApproveSuccess, refetchApproval]);

  // Handle list success
  useEffect(() => {
    if (isListSuccess) {
      onSuccess();
      handleClose();
    }
  }, [isListSuccess]);

  // Reset on close
  const handleClose = () => {
    setContractAddress('');
    setTokenId('');
    setPrice('');
    setError('');
    setStep('contract');
    setApproveTxHash(undefined);
    setListTxHash(undefined);
    setNftMetadata(null);
    setNftImage(null);
    onClose();
  };

  // Auto-advance when owner is confirmed
  useEffect(() => {
    if (step === 'details' && isOwner === true && tokenId) {
      // setStep('price');
    } else if (step === 'details' && isOwner === false && nftOwner) {
      setError('You are not the owner of this NFT');
    }
  }, [isOwner, step, tokenId, nftOwner]);

  // Auto-advance when approved
  useEffect(() => {
    if (step === 'approve' && isApproved) {
      setStep('list');
    }
  }, [isApproved, step]);

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[#FF8A3C]">List NFT for Sale</SheetTitle>
          <SheetDescription>
            List your NFT on the marketplace
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-0 px-4">
          {/* Step 1: Contract Address */}
          {step === 'contract' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="contract-address">NFT Contract Address</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-sidebar border border-border rounded-lg cursor-pointer hover:border-[#FF8A3C]/50 transition-colors" onClick={handleUseLarissaDaoNft}>
                    <RiCheckLine className="size-5 text-[#FF8A3C]" />
                    <div>
                      <div className="font-medium">Do you want to sell Larissa Dao NFT?</div>
                      <div className="text-xs text-muted-foreground">Click to auto-fill contract address</div>
                    </div>
                  </div>
                  <Input
                    id="contract-address"
                    placeholder="0x..."
                    value={contractAddress}
                    onChange={(e) => {
                      setContractAddress(e.target.value);
                      setError('');
                    }}
                    className="font-mono"
                  />
                </div>
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400">
                  <RiAlertLine className="size-4" />
                  {error}
                </div>
              )}
              <Button
                onClick={handleContractSubmit}
                className="w-full bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                disabled={!contractAddress}
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2: Token ID and Details */}
          {step === 'details' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="token-id">Token ID</Label>
                <Input
                  id="token-id"
                  type="number"
                  placeholder="Enter token ID"
                  value={tokenId}
                  onChange={(e) => {
                    setTokenId(e.target.value);
                    setError('');
                  }}
                  className="mt-2"
                />
              </div>

              {tokenId && (
                <div className="space-y-3">
                  {isLoadingOwner || isLoadingURI ? (
                    <div className="flex items-center justify-center py-8">
                      <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                    </div>
                  ) : (
                    <>
                      {nftImage && (
                        <Card className="bg-sidebar border border-border">
                          <CardContent className="p-0 m-0">
                            <div className="bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                              <img
                                src={nftImage}
                                alt={`NFT #${tokenId}`}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            {nftMetadata?.name && (
                              <div className="px-4 font-medium mb-1">{nftMetadata.name}</div>
                            )}
                            {nftMetadata?.description && (
                              <div className="px-4 text-sm text-muted-foreground">{nftMetadata.description}</div>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Owner:</span>
                          <span className="font-mono">{nftOwner ? `${nftOwner.slice(0, 6)}...${nftOwner.slice(-4)}` : 'Loading...'}</span>
                        </div>
                        {isOwner === true && (
                          <Badge className="bg-green-500/10 text-green-400 border-green-500">
                            <RiCheckLine className="size-3 mr-1" />
                            You own this NFT
                          </Badge>
                        )}
                        {isOwner === false && (
                          <Badge variant="destructive">
                            <RiAlertLine className="size-3 mr-1" />
                            You don't own this NFT
                          </Badge>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400">
                  <RiAlertLine className="size-4" />
                  {error}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('contract')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleTokenIdSubmit}
                  className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                  disabled={!tokenId || isLoadingOwner || isOwner === false}
                >
                  {isLoadingOwner ? (
                    <>
                      <RiLoader4Line className="size-4 animate-spin mr-2" />
                      Checking...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Price */}
          {step === 'price' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="price">Price (LRS)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.0001"
                  placeholder="0.0"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    setError('');
                  }}
                  className="mt-2"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Enter the price in LRS tokens
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400">
                  <RiAlertLine className="size-4" />
                  {error}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('details')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handlePriceSubmit}
                  className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                  disabled={!price}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Approve */}
          {step === 'approve' && (
            <div className="space-y-4">
              <div className="p-4 bg-sidebar border border-border rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Contract:</span>
                    <span className="font-mono text-xs">{contractAddress.slice(0, 10)}...{contractAddress.slice(-8)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Token ID:</span>
                    <span className="font-medium">#{tokenId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Price:</span>
                    <span className="font-medium text-[#FF8A3C]">{price} LRS</span>
                  </div>
                </div>
              </div>

              {isApproved ? (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400">
                    <RiCheckLine className="size-5" />
                    <span>NFT is already approved for marketplace</span>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-400 mb-2">
                    <RiAlertLine className="size-5" />
                    <span>Approval Required</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    You need to approve the marketplace contract to transfer your NFT.
                  </p>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400">
                  <RiAlertLine className="size-4" />
                  {error}
                </div>
              )}

              {approveTxHash && (
                <div className="p-4 bg-sidebar border border-border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Transaction Hash:</div>
                  <div className="font-mono text-xs break-all">{approveTxHash}</div>
                  {isApprovePending && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-[#FF8A3C]">
                      <RiLoader4Line className="size-4 animate-spin" />
                      Waiting for confirmation...
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('price')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleApprove}
                  className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                  disabled={isApproved || isApprovePending || !!approveTxHash}
                >
                  {isApprovePending ? (
                    <>
                      <RiLoader4Line className="size-4 animate-spin mr-2" />
                      Approving...
                    </>
                  ) : isApproved ? (
                    <>
                      <RiCheckLine className="size-4 mr-2" />
                      Approved
                    </>
                  ) : (
                    'Approve NFT'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: List */}
          {step === 'list' && (
            <div className="space-y-4">
              <div className="p-4 bg-sidebar border border-border rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Contract:</span>
                    <span className="font-mono text-xs">{contractAddress.slice(0, 10)}...{contractAddress.slice(-8)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Token ID:</span>
                    <span className="font-medium">#{tokenId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Price:</span>
                    <span className="font-medium text-[#FF8A3C]">{price} LRS</span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400">
                  <RiAlertLine className="size-4" />
                  {error}
                </div>
              )}

              {listTxHash && (
                <div className="p-4 bg-sidebar border border-border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Transaction Hash:</div>
                  <div className="font-mono text-xs break-all">{listTxHash}</div>
                  {isListPending && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-[#FF8A3C]">
                      <RiLoader4Line className="size-4 animate-spin" />
                      Listing NFT...
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('approve')}
                  className="flex-1"
                  disabled={isListPending}
                >
                  Back
                </Button>
                <Button
                  onClick={handleListNft}
                  className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                  disabled={!isApproved || isListPending || !!listTxHash}
                >
                  {isListPending ? (
                    <>
                      <RiLoader4Line className="size-4 animate-spin mr-2" />
                      Listing...
                    </>
                  ) : (
                    'List NFT'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
